// ════════════════════════════════════════════════════════════════════
// TERROR — THE BRAIN RESOLVES TOMES THROUGH THE PLAYER'S RESOLVER
// ────────────────────────────────────────────────────────────────────
// ONE RESOLVER (2026-09-15). Until today a Tome's effect existed TWICE:
// once in src/tomeResolver.js (the player's side, extracted from the
// Shatterfield on 2026-08-27) and once as a data row + a ~700-line step
// executor in NPC.js (the brain's side). They agreed by hand and by
// test, never by construction — the referee's book called it "the
// largest structural fault in the codebase" and caP'n put the collapse
// at the top of the list: "we already tried building an engine that made
// sure everything was cohesive but yeah definitely needs some work if all
// this slipping happened."
//
// This file is the collapse. The brain calls the SAME `applyTomeEffect`
// the human's board calls, with a ctx whose three human-facing seams are
// answered by the brain instead of by React:
//
//   setTargetPicker  — the board opens a modal and continues in onPick.
//                      Here every picker is queued, scored by
//                      `chooseForBrain`, and answered by calling onPick
//                      exactly the way PickerOptionCard does (the RAW
//                      element: a string for a `cards:` pool, an object
//                      for an `options:` list — see tools/cardText).
//   setSgModal       — the Shadow Gambling modal. Rolled here the way the
//                      modal rolls (ShadowGamblingModal: d6 per die, a
//                      chosen number, `autoWin` honoured; coin 50/50).
//   showToast        — captured, never shown; the balancer's probes read
//                      the resolver's own tomeProbe instead.
//
// `updatePlayer` is gameManager's — the single Firebase touchpoint — so
// R08's opening-Cycle seal, the pooled 2v2 Anima redirect and R123's
// ward (which tomeResolver shadows onto ctx.updatePlayer itself) all bind
// for the brain exactly as they bind for the human. After every write the
// ctx's matchRef is re-read, because the resolver reads live state from
// `ctx.matchRef.current` between its own writes and the board's listener
// is not here to refresh it.
//
// ⚠ WHAT THIS DOES NOT DO. Feeding a Treasure (R129) is not a Tome
// effect — the player feeds through ATVFeedModal, the brain through
// NPC.execFeed — so feed rows still take the brain's own path. Their
// numbers were unified in rules.ATV_FEED on 2026-09-15 (morning). That
// pair is the one second copy left, and it is named in the referee's book.
// ════════════════════════════════════════════════════════════════════
import { applyTomeEffect } from "./tomeResolver";
import { getMatch, updatePlayer } from "./gameManager";
import { ESCENDANTS, TOMES, IMPERA, UHLTIS, AFFINITIES, PROPHECIES, CURIOS, ESCLINGS } from "./cards";
import { baseId } from "./necroSeal";

const ALL = [...ESCENDANTS, ...TOMES, ...IMPERA, ...UHLTIS, ...AFFINITIES, ...PROPHECIES, ...CURIOS, ...ESCLINGS];
const getCard = (id) => (id ? ALL.find((c) => c.id === baseId(id)) || null : null);

// ── THE SCORER ──────────────────────────────────────────────────────
// A picker is one of two shapes. `cards:` is a pool of ids the seat is
// choosing FROM ITS OWN piles (a tutor, a bet); `options:` is a list of
// objects with `id` / `escendantId` / `slotIdx` / `label`, aimed at either
// side. The title says which side: a ruin / seize / nullify is aimed at
// the adversary and the brain takes the most valuable thing on offer; a
// resurrect / slot / ward / lend / mode is the brain's own and it takes
// its strongest being or the first sensible option; a "send to
// Necropolis" is a self-cost and it gives up the least.
//
// Deterministic on purpose (no dice): the balancer's seeds and the
// tomebook selftest need the same board to yield the same pick.
const ENEMY_RE = /ruin|seize|steal|nullify|disease|adversary|opponent|executioner|superque|dagger|non-being/i;
const COST_RE  = /send 1 (card|being) to necropolis|sacrifice|discard|give up/i;
const SLOT_RE  = /choose slot|choose mode|choose 1 blood|guesses/i;

// `side` is the seat whose rifts an option's `slotIdx` indexes — the
// adversary's for a ruin, the brain's own for a ward or a resurrection.
export const valueOfOption = (opt, m, side) => {
  if (opt == null) return 0;
  if (typeof opt === "string") return valueOfCardId(opt, m, side);
  const card = getCard(opt.escendantId || opt.cardId || opt.tomeId || opt.id);
  let v = card ? (Number(card.level) || 1) * 3 : 0;
  // A being that already holds what the picker grants is worth nothing to
  // it (Boots of Wing Walking on an Intangible being re-stamps a flag).
  {
    const own = m?.players?.[opt.seat ?? side]?.field?.esclings;
    const s = Array.isArray(own) && opt.slotIdx != null ? own[opt.slotIdx] : null;
    if (s?.intangible) v -= 50;
  }
  if (card?.type === "U'hlti") v += 6;
  if (card?.type === "Affinity") v += 9;
  // A being's Soul is stored value — a decorated Lord outranks a bare one.
  const rifts = m?.players?.[opt.seat ?? side]?.field?.esclings;
  const slot = Array.isArray(rifts) && opt.slotIdx != null ? rifts[opt.slotIdx] : null;
  if (slot?.soul) v += slot.soul.length * 2;
  if (Array.isArray(opt.soul)) v += opt.soul.length * 2;
  return v;
};

const valueOfCardId = (id, m, seat) => {
  const card = getCard(id);
  if (!card) return 0;
  const lvl = Number(card.level) || 1;
  const rifts = m?.players?.[seat]?.field?.esclings || [];
  if (card.type === "Escendant") {
    // Prefer what the brain can actually PLAY next: an L1 for an empty
    // rift, or the next level up for a being it already holds.
    const holdsPrev = rifts.some((s) => {
      const c = getCard(s?.escendant);
      return c && c.level === lvl - 1 && c.ilk === card.ilk && c.school === card.school;
    });
    const hasEmpty = rifts.some((s) => s && !s.escendant);
    if (lvl === 1 && hasEmpty) return 30 + lvl;
    if (lvl > 1 && holdsPrev) return 40 + lvl * 3;
    return 10 + lvl;
  }
  if (card.type === "Tome") return 20 + lvl * 2;
  if (card.type === "Impera") return 15 + lvl * 2;
  if (card.type === "U'hlti") return 25;
  return 5;
};

export function chooseForBrain(picker, m, seat, oppSeat = seat === 0 ? 1 : 0) {
  if (!picker) return null;
  const title = String(picker.title || "");
  const enemy = ENEMY_RE.test(title) && !/resurrect|holds the ward|lend/i.test(title);
  const side = enemy ? oppSeat : seat;
  if (Array.isArray(picker.cards) && picker.cards.length) {
    const pool = picker.cards.filter((x) => x != null);
    if (!pool.length) return null;
    return pool.reduce((best, x) => (valueOfCardId(x, m, seat) > valueOfCardId(best, m, seat) ? x : best), pool[0]);
  }
  if (Array.isArray(picker.options) && picker.options.length) {
    const opts = picker.options.filter((x) => x != null);
    if (!opts.length) return null;
    if (SLOT_RE.test(title)) return opts[0];
    const score = (o) => valueOfOption(o, m, side);
    if (COST_RE.test(title)) return opts.reduce((best, o) => (score(o) < score(best) ? o : best), opts[0]);
    return opts.reduce((best, o) => (score(o) > score(best) ? o : best), opts[0]);
  }
  return null;
}

// ── THE GAMBLE, ROLLED THE WAY THE MODAL ROLLS ──────────────────────
// ShadowGamblingModal: `acb` rolls `diceCount` d6 against a number the
// GAMBLER chose (one die must match; two dice must both match); `coin`
// is heads/tails; `guess_level` is the ADVERSARY guessing 1–3. `autoWin`
// (an SG streak card) wins outright, exactly as on the board.
// ⚠ ONE HONEST APPROXIMATION, unchanged from the brain's old executor:
// when the brain gambles, the HUMAN adversary is not asked to choose or
// guess — the number is rolled here. That is what shipped before the
// collapse and it is not made worse by it; opening a real prompt on the
// human's client is the day ACB is wired through the response bridge.
export async function settleGambleAsBrain(sg, rand = Math.random) {
  const d6 = () => Math.floor(rand() * 6) + 1;
  if (sg.type === "acb") {
    const chosen = d6();
    const dice = Array.from({ length: sg.diceCount || 1 }, d6);
    const won = sg.autoWin ? true : ((sg.diceCount || 1) === 1 ? dice[0] === chosen : dice.every((d) => d === chosen));
    await sg.onResolve?.(won, dice, chosen);
    return { won, dice, chosen };
  }
  if (sg.type === "coin") {
    const final = rand() > 0.5 ? "heads" : "tails";
    const won = sg.autoWin ? true : final === "heads";
    await sg.onResolve?.(won, final);
    return { won, final };
  }
  if (sg.type === "guess_level") {
    const guess = Math.floor(rand() * 3) + 1;
    const won = await sg.onResolve?.(guess, !!sg.autoWin);
    return { won: !!won, guess };
  }
  await sg.onResolve?.(false);
  return { won: false };
}

// ── THE DOOR ────────────────────────────────────────────────────────
// Resolve `card` as `seat`, against `oppSeat`, through the ONE resolver.
// Returns the resolver's sentinel ("AON_RUINED" and friends), the toasts
// it would have shown, how many pickers were answered, and the live match
// after the last write — so a caller can rebase its own drafts from it.
// ⚠ TWO PICKER STYLES LIVE IN THE RESOLVER AND BOTH ARE ANSWERED.
// Most branches open a picker and RETURN, continuing in onPick (the
// board's modal style); a few AWAIT a promise the picker resolves. So a
// picker is answered the moment it opens — on a microtask, so the code
// after the setTargetPicker call runs first, exactly as it does under
// React — and every in-flight answer is awaited after the resolver
// returns, including answers that opened further pickers.
export async function resolveTomeAsBrain(code, card, { seat, oppSeat, rand = Math.random, maxPicks = 32 } = {}) {
  const first = await getMatch(code);
  const matchRef = { current: first };
  const inflight = [];
  const toasts = [];
  let picks = 0;
  const answer = (fn) => {
    if (picks >= maxPicks) return;
    picks++;
    inflight.push(new Promise((res) => queueMicrotask(() => Promise.resolve().then(fn).then(res, res))));
  };
  const ctx = {
    code, playerIdx: seat, oppIdx: oppSeat,
    match: first, matchRef,
    me: first?.players?.[seat] || {}, opponent: first?.players?.[oppSeat] || {},
    showToast: (t) => { toasts.push(String(t)); },
    setTargetPicker: (pk) => {
      if (!pk) return;
      answer(async () => {
        const opt = chooseForBrain(pk, matchRef.current, seat, oppSeat);
        if (opt == null) return;            // nothing to pick — the effect stops where the board would
        await pk.onPick(opt);
      });
    },
    setSgModal: (sg) => { if (sg) answer(() => settleGambleAsBrain(sg, rand)); },
    factionStatsRef: { current: { nihil: 0, omnia: 0, ancient: 0, otherTomes: 0 } },
    responseWindowRef: { current: null },
    updatePlayer: async (c, i, u, opts) => {
      const r = await updatePlayer(c, i, u, opts);
      matchRef.current = await getMatch(c);
      return r;
    },
  };
  const sentinel = await applyTomeEffect(card, ctx);
  while (inflight.length) await inflight.shift();
  return { sentinel, toasts, picks, match: matchRef.current };
}

export default { resolveTomeAsBrain, chooseForBrain, settleGambleAsBrain, valueOfOption };
